import CardContainer from "components/CardContainer/CardContainer";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import tableStyles from "styles/shared/table.module.scss";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";

interface SupportResource {
  productType: string;
  documents: Array<{
    name: string;
    url: string;
  }>;
  videos: Array<{
    name: string;
    url: string;
  }>;
}

const Support = () => {
  const supportResources: SupportResource[] = [
    {
      productType: "Web Portal",
      documents: [
        {
          name: "User Manual",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20Web%20Portal%20User%20Manual.pdf"
        }
      ],
      videos: [
        {
          name: "Web Portal Tutorial",
          url: "https://vimeo.com/1079626709/d8f19160a8"
        },
        {
          name: "Overview",
          url: "https://vimeo.com/1123016939/33d82c759c"
        },
        {
          name: "Account Setup",
          url: "https://vimeo.com/1123017355/88ec4745f7"
        },
        {
          name: "Account & Clinician Dashboards",
          url: "https://vimeo.com/1123017236/a61c4a39d5"
        },
        {
          name: "Create Patients & Orders",
          url: "https://vimeo.com/1123017543/d5d23da8ed"
        }
      ]
    },
    {
      productType: "Lab In Clinic",
      documents: [
        {
          name: "User Manual",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20In%20Clinic%20User%20Manual.pdf"
        },
        {
          name: "Billing & Coding Guidelines",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20In%20Clinic%20Billing%20and%20Coding%20Guidelines.pdf"
        }
      ],
      videos: [
        {
          name: "Lab in Clinic Tutorial",
          url: "https://vimeo.com/1079567097/4ec6f78106"
        },
        {
          name: "Gait Testing",
          url: "https://vimeo.com/1123016071/cc15256e74"
        },
        {
          name: "Balance Testing",
          url: "https://vimeo.com/1123015057/9f7c8908d8"
        },
        {
          name: "Angles Testing",
          url: "https://vimeo.com/1079487636/bc64508396"
        }
      ]
    },
    {
      productType: "Lab At Home",
      documents: [
        {
          name: "User Manual",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20At%20Home%20User%20Guide.pdf"
        },
        {
          name: "Billing & Coding Guidelines",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20At%20Home%20Billing%20and%20Coding%20Guidelines.pdf"
        },
        {
          name: "Patient Handout",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20at%20Home%20Patient%20Brochure.pdf"
        },
        {
          name: "RKM Order Instructions",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20at%20Home%20RKM%20Order%20Instructions.pdf"
        }
      ],
      videos: [
        {
          name: "Lab at Home Tutorial",
          url: "https://vimeo.com/1079550512/0644320467"
        },
        {
          name: "Getting Started",
          url: "https://vimeo.com/1123018389/3cef22b7b0"
        },
        {
          name: "App Navigation",
          url: "https://vimeo.com/1123019323/95a7a255b0"
        },
        {
          name: "Gait Testing",
          url: "https://vimeo.com/1123019583/f281ad654b"
        },
        {
          name: "Balance Testing",
          url: "https://vimeo.com/1123019432/461d053010"
        },
        {
          name: "Sensor Technology",
          url: "https://vimeo.com/1123019711/d1423aa393"
        },
        {
          name: "Test Metrics",
          url: "https://vimeo.com/1079564404/9ea9f75e48"
        }
      ]
    },
    {
      productType: "Lab Remote Assist",
      documents: [
        {
          name: "User Manual",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20Remote%20Assist%20User%20Guide.pdf"
        },
        {
          name: "Billing & Coding Guidelines",
          url: "https://s3.amazonaws.com/clinical-prod-app/support/manual/BioMech%20Lab%20Remote%20Assist%20Billing%20and%20Coding%20Guidelines.pdf"
        }
      ],
      videos: [
        {
          name: "Lab Remote Assist Tutorial",
          url: "https://vimeo.com/1123018055/c65d1a6641"
        }
      ]
    }
  ];

  return (
    <div className="h-full flex flex-col relative pr-5 overflow-auto pb-3">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1 w-full">
          <div className="text-2xl font-bold text-cloudBurst">Support</div>
        </div>
        
        <CardContainer title="Product Resources" withoutOpacity>
          <TableContainer 
            className={`${tableStyles.shared_table_container} ${tableStyles.padding_16_20}`}
            sx={{ overflow: 'auto' }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>PRODUCT TYPE</TableCell>
                  <TableCell>DOCUMENTS</TableCell>
                  <TableCell>VIDEOS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {supportResources.map((resource) => (
                  <TableRow key={resource.productType}>
                    <TableCell className="align-top">
                      <div className="font-semibold text-cloudBurst text-base">
                        {resource.productType}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <ul className="flex flex-col gap-1 list-none">
                        {resource.documents.map((doc) => (
                          <li key={doc.name} className="flex items-center gap-2">
                            <ArticleOutlinedIcon sx={{ color: '#1E87C9', fontSize: 16 }} />
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pelorous hover:underline"
                            >
                              {doc.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                    <TableCell className="align-top">
                      <ul className="flex flex-col gap-1 list-none">
                        {resource.videos.map((video) => (
                          <li key={video.name} className="flex items-center gap-2">
                            <OndemandVideoOutlinedIcon sx={{ color: '#1E87C9', fontSize: 16 }} />
                            <a
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-pelorous hover:underline"
                            >
                              {video.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContainer>

        <CardContainer title="Still Have Questions?" withoutOpacity>
          <div className="text-center">
            <div className="text-cloudBurst mb-4">
              Reach out to our responsive customer service team:
            </div>
            <div className="flex flex-col gap-2">
              <div className="text-lg font-semibold text-cloudBurst">
                866-246-9999
              </div>
              <div className="text-pelorous">
                support@BioMechHealth.com
              </div>
            </div>
          </div>
        </CardContainer>
      </div>
    </div>
  );
};

export default Support;